import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Gift } from "lucide-react";
import { CTA_URL } from "../data/site";
import { scrollToId } from "../lib/scroll";
import { trackSubscribe } from "../lib/pixel";
import { EASE } from "./Reveal";

function Mask({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const panelY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} id="hero" className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-36">
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 right-[-12%] h-[460px] w-[460px] rounded-full bg-emerald/15 blur-[130px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[-25%] left-[-10%] h-[380px] w-[380px] rounded-full bg-emerald/10 blur-[110px]"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-12">
        <div className="text-center">
          <Mask delay={0.05}>
            <p className="inline-flex items-center rounded-full border border-goldlight/30 bg-goldlight/5 px-4 py-2 text-[10px] font-bold tracking-[0.28em] text-goldlight sm:text-xs">
              TRADING KALIAN. REWARD KALIAN.
            </p>
          </Mask>
          <Mask delay={0.16}>
            <p className="mx-auto mt-6 max-w-md text-base font-light text-sage md:text-lg">
              &ldquo;Kalau trading bisa memberi nilai lebih, kenapa nggak gandakan profitnya?&rdquo;
            </p>
          </Mask>
          <h1 className="mt-5 font-black leading-[0.95] tracking-tight">
            <Mask delay={0.28}>
              <span className="block text-5xl text-cream sm:text-6xl xl:text-7xl">BE YOUR OWN</span>
            </Mask>
            <Mask delay={0.4}>
              <span className="block text-5xl text-gold sm:text-6xl xl:text-7xl">
                IB<span className="text-cream">!!</span>
              </span>
            </Mask>
          </h1>
          <Mask delay={0.52}>
            <p className="mx-auto mt-6 max-w-md text-sm font-light leading-relaxed text-sage md:text-base">
              Dulu, kalian trading untuk mengejar profit. Sekarang, rebate juga bakal jadi milik kalian.
            </p>
            <p className="mx-auto mt-3 max-w-md text-sm font-light leading-relaxed text-sage md:text-base">
              Dapatkan <span className="font-bold text-gold">rebate USD 15 per lot</span> melalui akun BOOSTER setiap
              kali kalian trade. <span className="font-bold text-goldlight">COLLECT YOUR REWARD. FREE OF CHARGE!</span>
            </p>
          </Mask>
          <Mask delay={0.64}>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                data-testid="hero-primary-cta"
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackSubscribe}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-bold text-night shadow-[0_16px_40px_-12px_rgba(212,175,55,0.45)] transition-all duration-300 hover:scale-[1.03] hover:bg-goldlight"
              >
                Chat Sini Buat GANDAKAN KEUNTUNGAN. GRATIS!
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <button
                data-testid="hero-secondary-cta"
                onClick={() => scrollToId("hadiah")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/20 px-6 py-4 text-sm font-bold text-cream transition-colors duration-300 hover:border-goldlight hover:text-goldlight"
              >
                <Gift className="h-4 w-4" />
                Lihat Hadiah
              </button>
            </div>
          </Mask>
        </div>

        <motion.div
          style={{ y: panelY }}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
        >
          <div
            data-testid="rebate-panel"
            className="rounded-[1.75rem] border border-gold/25 bg-forest p-5 text-center shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] sm:p-6"
          >
            <p className="text-[10px] font-bold tracking-[0.3em] text-goldlight sm:text-[11px]">REBATE PER LOT</p>
            <p className="mt-1.5 text-5xl font-black leading-none text-gold sm:text-6xl">
              $15<span className="ml-2 align-top text-xl font-bold text-cream/70 sm:text-2xl">USD</span>
            </p>
            <p className="mt-2.5 text-[9px] font-bold uppercase tracking-[0.18em] text-sage/80 sm:text-[10px]">
              Setiap lot punya nilai lebih. UNLIMITED REWARDS
            </p>
            <div
              data-testid="prize-stage"
              className="mt-4 overflow-hidden rounded-2xl border border-gold/20 bg-[#080D0B]"
            >
              <img
                src="/images/stage-prizes.jpg"
                alt="Panggung hadiah BOOSTER: rumah di belakang, empat mobil Mercedes-Benz di tengah, deretan motor Honda Vario dan Kawasaki Ninja di kedua sisi, serta iPhone, iPad, MacBook, AirPods, Apple Watch, dan liburan ke Tokyo di barisan depan atas podium hitam dengan trim emas"
                className="h-auto w-full"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
